import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import { Button, Form, Card, Alert, Row, Col } from 'react-bootstrap';
import { createFooterInstance } from '../../../Models/Footer';
import { toast, ToastContainer } from 'react-toastify';
import { SketchPicker } from 'react-color';
import MediaSelector from '../Media/MediaSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash, faImage, faGlobe } from '@fortawesome/free-solid-svg-icons';
import { LanguageService } from '../../../Services/Axios/Api';
import LanguageSelector from '../../../Components/LanguageSelector/LanguageSelector';
import { availableAnimations } from '../../../Services/Animations/availableAnimations';
import ControlledRichTextEditor from '../../../Components/ControlledRichTextEditor/ControlledRichTextEditor';

const FooterBuilder = () => {
    const [footerInstance, setFooterInstance] = useState(createFooterInstance());
    const [logo, setLogo] = useState(footerInstance.getLogo());
    const [logoText, setLogoText] = useState(footerInstance.getLogoText());
    const [importantLinks, setImportantLinks] = useState(footerInstance.getImportantLinks());
    const [socialLinks, setSocialLinks] = useState(footerInstance.getSocialLinks());
    const [contactInfo, setContactInfo] = useState(footerInstance.getContactInfo());
    const [copyright, setCopyright] = useState(footerInstance.getData().copyright || '');
    
    // Form states
    const [newImportantLink, setNewImportantLink] = useState({ name: '', path: '' });
    const [newSocialLink, setNewSocialLink] = useState({ name: '', url: '' });
    const [editingLink, setEditingLink] = useState(null);
    const [editedLink, setEditedLink] = useState({ name: '', path: '' });
    
    // Style states
    const [showBgColorPicker, setShowBgColorPicker] = useState(false);
    const [showTextColorPicker, setShowTextColorPicker] = useState(false);
    const [showTextHoverColorPicker, setShowTextHoverColorPicker] = useState(false);
    const [currentStyle, setCurrentStyle] = useState(footerInstance.getStyle());
    const [currentAnimation, setCurrentAnimation] = useState(footerInstance.getAnimation());
    const [showMediaSelector, setShowMediaSelector] = useState(false);
    
    // Language states
    const [currentLanguage, setCurrentLanguage] = useState('');
    const [availableLanguages, setAvailableLanguages] = useState([]);   
    const [languageAlertType, setLanguageAlertType] = useState('info');
    const [showLanguageAlert, setShowLanguageAlert] = useState(false);
    const [isLanguagesLoaded, setIsLanguagesLoaded] = useState(false);

    useEffect(() => {
        loadLanguages();
    }, []);

    // Load data after languages are loaded and current language is set
    useEffect(() => {
        if (isLanguagesLoaded && currentLanguage) {
            getData();
        }
    }, [isLanguagesLoaded, currentLanguage]);

    const getData = async () => {
        try {
            console.log('FooterBuilder: Loading data for language:', currentLanguage);
            await footerInstance.load(currentLanguage);
            
            setLogo(footerInstance.getLogo());
            setLogoText(footerInstance.getLogoText());
            setImportantLinks(footerInstance.getImportantLinks());
            setSocialLinks(footerInstance.getSocialLinks());
            setContactInfo(footerInstance.getContactInfo());
            setCurrentStyle(footerInstance.getStyle());
            setCurrentAnimation(footerInstance.getAnimation());
            setCopyright(footerInstance.getData().copyright || '');
        } catch (error) {
            console.error('Error loading footer data:', error);
            toast.error('Failed to load footer data');
        }
    };

    const loadLanguages = async () => {
        try {
            const response = await LanguageService.getLanguages();
            setAvailableLanguages(response.result);
            
            // Set default language from API
            const defaultLanguage = response.result.find(lang => lang.isDefault)?.code || 'en';
            setCurrentLanguage(defaultLanguage);
            setIsLanguagesLoaded(true);
            
            console.log('FooterBuilder: Language loaded:', defaultLanguage);
        } catch (error) {
            console.error('Error loading languages:', error);
            toast.error('Failed to load languages');
            setCurrentLanguage('en');
            setIsLanguagesLoaded(true);
        }
    };

    const handleLogoSelect = (selectedPath) => {
        if (selectedPath) {
            setLogo(selectedPath);
            footerInstance.setLogo(selectedPath);
        }
    };

    // Important Links Handlers
    const addImportantLink = () => {
        if (!newImportantLink.name || !newImportantLink.path) return;
        
        const link = {
            id: Date.now(),
            ...newImportantLink
        };
        
        footerInstance.addImportantLink(link);
        setImportantLinks(footerInstance.getImportantLinks());
        setNewImportantLink({ name: '', path: '' });
    };

    const removeImportantLink = (id) => {
        footerInstance.removeImportantLink(id);
        setImportantLinks(footerInstance.getImportantLinks());
    };

    const startEditingLink = (link) => {
        setEditingLink(link.id);
        setEditedLink({ name: link.name, path: link.path });
    };

    const saveEditedLink = (id) => {
        footerInstance.setImportantLinks(
            importantLinks.map(link => 
                link.id === id ? { ...link, ...editedLink } : link
            )
        );
        setImportantLinks(footerInstance.getImportantLinks());
        setEditingLink(null);
    };

    // Social Links Handlers
    const addSocialLink = () => {
        if (!newSocialLink.name || !newSocialLink.url) return;
        
        const link = {
            id: Date.now(),
            ...newSocialLink
        };
        
        footerInstance.addSocialLink(link);
        setSocialLinks(footerInstance.getSocialLinks());
        setNewSocialLink({ name: '', url: '' });
    };

    const removeSocialLink = (id) => {
        footerInstance.removeSocialLink(id);
        setSocialLinks(footerInstance.getSocialLinks());
    };

    // Contact Info Handlers
    const handleContactInfoChange = (e) => {
        const { name, value } = e.target;
        setContactInfo({
            ...contactInfo,
            [name]: value
        });
    };

    // Style Handlers
    const handleBgColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            Footer: {
                ...currentStyle.Footer,
                backgroundColor: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        footerInstance.setStyle(updatedStyle);
    };

    const handleTextColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            FooterText: {
                ...currentStyle.FooterText,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        footerInstance.setStyle(updatedStyle);
    };

    const handleTextHoverColorChange = (color) => {
        const updatedStyle = {
            ...currentStyle,
            FooterTextHover: {
                ...currentStyle.FooterTextHover,
                color: color.hex
            }
        };
        setCurrentStyle(updatedStyle);
        footerInstance.setStyle(updatedStyle);
    };

    const handleSaveChanges = async () => {
        try {
            // Update all data before saving
            footerInstance.setLogo(logo);
            footerInstance.setLogoText(logoText);
            footerInstance.setContactInfo(contactInfo);
            footerInstance.setData({
                ...footerInstance.getData(),
                copyright: copyright
            });
            footerInstance.setAnimation(currentAnimation);
            
            await footerInstance.save();
            toast.success("Changes saved successfully!");
            getData();
        } catch (error) {
            toast.error("Failed to save changes. Please try again.");
        }
    };

    // Animation handlers
    const handleAnimationTypeChange = (e) => {
        const value = e.target.value;
        const updated = { ...currentAnimation, animationType: value };
        setCurrentAnimation(updated);
        footerInstance.setAnimation(updated);
    };
    const handleAnimationDurationChange = (e) => {
        const value = e.target.value;
        const updated = { ...currentAnimation, animationDuration: value };
        setCurrentAnimation(updated);
        footerInstance.setAnimation(updated);
    };
    const handleAnimationDelayChange = (e) => {
        const value = e.target.value;
        const updated = { ...currentAnimation, animationDelay: value };
        setCurrentAnimation(updated);
        footerInstance.setAnimation(updated);
    };

    const handleLanguageChange = async (newLanguage) => {
        setCurrentLanguage(newLanguage);
        setShowLanguageAlert(true);
        setLanguageAlertType('info');
        toast.success(`Switched to ${availableLanguages.find(l => l.code === newLanguage)?.name || newLanguage.toUpperCase()}`);
        setTimeout(() => setShowLanguageAlert(false), 3000);
        // getData will be called automatically by the useEffect when currentLanguage changes
    };

    // const handleCopyFromLanguage = async (languageCode) => {
    //     try {
    //         // const response = await PageService.CopyPageFromLanguage(slug, languageCode, currentLanguage );
    //         // toast.success(response.result);
    //         setTimeout(() => {
    //             // navigate(`/bj-admin/pages/page-builder/${id}?slug=${slug}&lang=${currentLanguage}`);
    //             getData();
    //         }, 1000);
    //     } catch (error) {
    //         console.error('Error copying from language:', error);
    //         toast.error('Failed to copy from language');
    //     }
    // };

    return (
        <div className="container mt-4">
            <h3 className="mb-3">Footer Builder</h3>
            
            {/* Language Management Card */}
            <Card className="mb-4 language-management-card">
                <Card.Header className="language-management-header">
                    <Row className="">
                        <Col md={6}>
                            <div className="d-flex align-items-center gap-3">
                                <FontAwesomeIcon icon={faGlobe} className="text-primary" />
                                <div>
                                    <h6 className="mb-0">Language Management</h6>
                                    <small className="text-muted">
                                        Managing content for: <strong>{availableLanguages.find(l => l.code === currentLanguage)?.name}</strong>
                                    </small>
                                </div>
                            </div>
                        </Col>
                        <Col md={6} className="d-flex justify-content-end">
                            <LanguageSelector
                                currentLanguage={currentLanguage}
                                availableLanguages={availableLanguages}
                                onLanguageChange={handleLanguageChange}
                                showBadge={false}
                                showCopyFromLanguage={false}
                                // onCopyFromLanguage={handleCopyFromLanguage}
                            />
                        </Col>
                    </Row>
                </Card.Header>
                
                {/* Language Alert */}
                {showLanguageAlert && (
                    <Card.Body className="p-3 language-alert-container">
                        <Alert
                            variant={languageAlertType} 
                            dismissible 
                            onClose={() => setShowLanguageAlert(false)}
                            className="mb-0"
                        >
                            <div className="d-flex align-items-center gap-2">
                                <FontAwesomeIcon icon={faGlobe} />
                                <div>
                                    {languageAlertType === 'warning' ? (
                                        <>
                                            <strong>Translation Required:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version. 
                                            {currentLanguage !== 'en' && ' Consider copying content from English to start with.'}
                                        </>
                                    ) : (
                                        <>
                                            <strong>Language Switched:</strong> You are now editing the {availableLanguages.find(l => l.code === currentLanguage)?.name} version of this page.
                                        </>
                                    )}
                                </div>
                            </div>
                        </Alert>
                    </Card.Body>
                )}
            </Card>

            <Accordion defaultActiveKey="0">
                {/* Logo Section */}
                <Accordion.Item eventKey="0">
                    <Accordion.Header>Logo</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <Card className="mb-3">
                                <Card.Header className="bg-light">
                                    <h6 className="mb-0">Logo Preview</h6>
                                </Card.Header>
                                <Card.Body>
                                    {logo ? (
                                        <div className="text-center">
                                            <div className="logo-preview-container mb-3 p-3 border rounded bg-white">
                                                <img 
                                                    src={logo} 
                                                    alt="Logo" 
                                                    style={{ 
                                                        maxHeight: '100px',
                                                        maxWidth: '240px',
                                                        objectFit: 'contain',
                                                        width: '100%',
                                                        height: 'auto'
                                                    }} 
                                                    className="img-fluid"
                                                />
                                            </div>
                                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                                                <Button 
                                                    variant="outline-primary" 
                                                    size="sm"
                                                    onClick={() => setShowMediaSelector(true)}
                                                >
                                                    <FontAwesomeIcon icon={faEdit} className="me-2" />
                                                    Change
                                                </Button>
                                                <Button 
                                                    variant="outline-danger" 
                                                    size="sm"
                                                    onClick={() => {
                                                        setLogo(null);
                                                        footerInstance.setLogo(null);
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                                                    Remove
                                                </Button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center p-4">
                                            <div className="mb-3">
                                                <FontAwesomeIcon 
                                                    icon={faImage} 
                                                    className="text-muted" 
                                                    style={{ fontSize: '3rem' }} 
                                                />
                                            </div>
                                            <p className="text-muted mb-3">No logo selected</p>
                                            <Button 
                                                variant="primary" 
                                                onClick={() => setShowMediaSelector(true)}
                                            >
                                                <FontAwesomeIcon icon={faPlus} className="me-2" />
                                                Select Logo
                                            </Button>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                {/* Logo Text Section */}
                <Accordion.Item eventKey="1">
                    <Accordion.Header>Text</Accordion.Header>
                    <Accordion.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Logo Text</Form.Label>
                            <ControlledRichTextEditor
                                value={logoText}
                                onChange={(html) => setLogoText(html)}
                                placeholder="Enter logo text..."
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Copyright Text</Form.Label>
                            <Form.Control
                                type="text"
                                value={copyright}
                                onChange={(e) => setCopyright(e.target.value)}
                            />
                        </Form.Group>
                    </Accordion.Body>
                </Accordion.Item>

                {/* Important Links Section */}
                <Accordion.Item eventKey="2">
                    <Accordion.Header>Important Links</Accordion.Header>
                    <Accordion.Body>
                        <Form className="mb-3">
                            <h5>Add New Link</h5>
                            <Form.Control
                                type="text"
                                placeholder="Link Name"
                                value={newImportantLink.name}
                                onChange={(e) => setNewImportantLink({...newImportantLink, name: e.target.value})}
                            />
                            <Form.Control
                                type="text"
                                placeholder="Link"
                                className="mt-2"
                                value={newImportantLink.path}
                                onChange={(e) => setNewImportantLink({...newImportantLink, path: e.target.value})}
                            />
                            <div className="d-flex justify-content-end mt-2">
                                <Button onClick={addImportantLink}>Add Link</Button>
                            </div>
                        </Form>

                        <h5>Current Links</h5>
                        {importantLinks.map((link) => (
                            <div key={link.id} className="mb-3 p-2 border rounded">
                                {editingLink === link.id ? (
                                    <div>
                                        <Form.Control
                                            type="text"
                                            value={editedLink.name}
                                            className="mb-2"
                                            onChange={(e) => setEditedLink({...editedLink, name: e.target.value})}
                                            placeholder="Link Name"
                                        />
                                        <Form.Control
                                            type="text"
                                            value={editedLink.path}
                                            className="mb-2"
                                            onChange={(e) => setEditedLink({...editedLink, path: e.target.value})}
                                            placeholder="Link "
                                        />
                                        <Button className="me-2" variant="success" onClick={() => saveEditedLink(link.id)}>Save</Button>
                                        <Button variant="secondary" onClick={() => setEditingLink(null)}>Cancel</Button>
                                    </div>
                                ) : (
                                    <>
                                        <p><strong>Name:</strong> {link.name}</p>
                                        <p><strong>Link:</strong> {link.path}</p>
                                        <div className="d-flex justify-content-end">
                                            <Button variant='warning' onClick={() => startEditingLink(link)}>Edit</Button>
                                            <Button className='ms-2' variant='danger' onClick={() => removeImportantLink(link.id)}>Delete</Button>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </Accordion.Body>
                </Accordion.Item>

                {/* Contact Info Section */}
                <Accordion.Item eventKey="3">
                    <Accordion.Header>Contact Information</Accordion.Header>
                    <Accordion.Body>
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Address</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="address"
                                    value={contactInfo.address}
                                    onChange={handleContactInfoChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Phone</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="phone"
                                    value={contactInfo.phone}
                                    onChange={handleContactInfoChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>WhatsApp</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="whatsapp"
                                    value={contactInfo.whatsapp}
                                    onChange={handleContactInfoChange}
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Email</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={contactInfo.email}
                                    onChange={handleContactInfoChange}
                                />
                            </Form.Group>
                        </Form>
                    </Accordion.Body>
                </Accordion.Item>

                {/* Social Links Section */}
                <Accordion.Item eventKey="4">
                    <Accordion.Header>Social Links</Accordion.Header>
                    <Accordion.Body>
                        <Form className="mb-3">
                            <h5>Add New Social Link</h5>
                            <Form.Select
                                value={newSocialLink.name}
                                onChange={(e) => setNewSocialLink({ ...newSocialLink, name: e.target.value })}
                                className="mb-2"
                            >
                                <option value="">Select Platform</option>
                                <option value="Facebook">Facebook</option>
                                <option value="Twitter">Twitter</option>
                                <option value="Instagram">Instagram</option>
                                <option value="Youtube">Youtube</option>
                            </Form.Select>
                            <Form.Control
                                type="text"
                                placeholder="Profile URL"
                                className="mt-2"
                                value={newSocialLink.url}
                                onChange={(e) => setNewSocialLink({...newSocialLink, url: e.target.value})}
                            />
                            <div className="d-flex justify-content-end mt-2">
                                <Button onClick={addSocialLink}>Add Social Link</Button>
                            </div>
                        </Form>

                        <h5>Current Social Links</h5>
                        {socialLinks.map((link) => (
                            <div key={link.id} className="mb-3 p-2 border rounded">
                                <p><strong>Platform:</strong> {link.name}</p>
                                <p><strong>URL:</strong> {link.url}</p>
                                <div className="d-flex justify-content-end">
                                    <Button className='ms-2' variant='danger' onClick={() => removeSocialLink(link.id)}>Delete</Button>
                                </div>
                            </div>
                        ))}
                    </Accordion.Body>
                </Accordion.Item>

                {/* Style Section */}
                <Accordion.Item eventKey="5">
                    <Accordion.Header>Style</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <label className="form-label">Background Color</label>
                            <div className="d-flex align-items-center">
                                <div 
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.Footer?.backgroundColor,
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowBgColorPicker(!showBgColorPicker)}
                                />
                                <span>{currentStyle.Footer?.backgroundColor}</span>
                            </div>
                            {showBgColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div 
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowBgColorPicker(false)}
                                    />
                                    <SketchPicker 
                                        color={currentStyle.Footer?.backgroundColor} 
                                        onChange={handleBgColorChange} 
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Text Color</label>
                            <div className="d-flex align-items-center">
                                <div 
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.FooterText.color,
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowTextColorPicker(!showTextColorPicker)}
                                />
                                <span>{currentStyle.FooterText.color}</span>
                            </div>
                            {showTextColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div 
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowTextColorPicker(false)}
                                    />
                                    <SketchPicker 
                                        color={currentStyle.FooterText.color} 
                                        onChange={handleTextColorChange} 
                                    />
                                </div>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="form-label">Text Hover Color</label>
                            <div className="d-flex align-items-center">
                                <div 
                                    style={{
                                        width: '30px',
                                        height: '30px',
                                        backgroundColor: currentStyle.FooterTextHover?.color,
                                        border: '1px solid #ccc',
                                        marginRight: '10px',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowTextHoverColorPicker(!showTextHoverColorPicker)}
                                />
                                <span>{currentStyle.FooterTextHover?.color}</span>
                            </div>
                            {showTextHoverColorPicker && (
                                <div style={{ position: 'absolute', zIndex: 2 }}>
                                    <div 
                                        style={{ position: 'fixed', top: '0', right: '0', bottom: '0', left: '0' }}
                                        onClick={() => setShowTextHoverColorPicker(false)}
                                    />
                                    <SketchPicker 
                                        color={currentStyle.FooterTextHover.color} 
                                        onChange={handleTextHoverColorChange} 
                                    />
                                </div>
                            )}
                        </div>
                    </Accordion.Body>
                </Accordion.Item>

                <Accordion.Item eventKey="6">
                    <Accordion.Header>Animation</Accordion.Header>
                    <Accordion.Body>
                        <div className="mb-3">
                            <label htmlFor="footerAnimationType" className="form-label">Animation Type</label>
                            <select 
                                id="footerAnimationType" 
                                className="form-select" 
                                value={currentAnimation.animationType}
                                onChange={handleAnimationTypeChange}
                            >
                                {availableAnimations.map((animation) => (
                                    <option value={animation}>{animation}</option>
                                ))}
                                
                            </select>
                        </div>

                        <div className="mb-3">
                            <label htmlFor="footerAnimationDuration" className="form-label">Duration (ms)</label>
                            <Form.Control 
                                id="footerAnimationDuration"
                                type="number"
                                min="0"
                                value={currentAnimation.animationDuration}
                                onChange={handleAnimationDurationChange}
                            />
                        </div>

                        <div className="mb-3">
                            <label htmlFor="footerAnimationDelay" className="form-label">Delay (ms)</label>
                            <Form.Control 
                                id="footerAnimationDelay"
                                type="number"
                                min="0"
                                value={currentAnimation.animationDelay}
                                onChange={handleAnimationDelayChange}
                            />
                        </div>
                    </Accordion.Body>
                </Accordion.Item>
            </Accordion>

            <div className="SaveChangesContainer d-flex justify-content-end mt-2">
                <ToastContainer />
                <Button variant='primary' onClick={handleSaveChanges}>Save Changes</Button>
            </div>

            <MediaSelector 
                isOpen={showMediaSelector}
                onClose={() => setShowMediaSelector(false)}
                onSelect={handleLogoSelect}
            />
        </div>
    );
};

export default FooterBuilder;